import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  FiSettings, FiSave, FiLock, FiShield, FiDownload, FiUpload, FiDatabase
} from 'react-icons/fi';
import { api } from '../../api';
import toast from 'react-hot-toast';

const AdminSettings = () => {
  const queryClient = useQueryClient();

  const { data: settingsResponse, isLoading } = useQuery('settings', () => api.getSettings());
  const settings = settingsResponse?.data || {};

  const [formData, setFormData] = useState({
    house_name: settings.house_name || '',
    welcome_message: settings.welcome_message || '',
    emergency_contact: settings.emergency_contact || '',
    house_manager_email: settings.house_manager_email || '',
    notification_email: settings.notification_email || '',
    enable_notifications: settings.enable_notifications || false
  });

  // Site password form state
  const [sitePasswordForm, setSitePasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Admin password form state
  const [adminPasswordForm, setAdminPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Import/Export state
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  React.useEffect(() => {
    if (settings) {
      setFormData({
        house_name: settings.house_name || 'Welcome to Our Mountain Retreat',
        welcome_message: settings.welcome_message || 'Your complete guide to enjoying your stay',
        emergency_contact: settings.emergency_contact || '',
        house_manager_email: settings.house_manager_email || '',
        notification_email: settings.notification_email || '',
        enable_notifications: settings.enable_notifications || false
      });
    }
  }, [settings]);

  const updateMutation = useMutation(
    (data) => api.updateSettings(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('settings');
        queryClient.invalidateQueries('publicSettings');
        toast.success('Settings updated successfully');
      },
      onError: () => toast.error('Failed to update settings')
    }
  );

  const sitePasswordMutation = useMutation(
    (data) => api.setSitePassword(data),
    {
      onSuccess: () => {
        toast.success('Site password updated successfully');
        setSitePasswordForm({ newPassword: '', confirmPassword: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update site password');
      }
    }
  );

  const adminPasswordMutation = useMutation(
    (data) => api.changePassword(data),
    {
      onSuccess: () => {
        toast.success('Admin password updated successfully');
        setAdminPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update admin password');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleSitePasswordSubmit = (e) => {
    e.preventDefault();
    if (sitePasswordForm.newPassword !== sitePasswordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (sitePasswordForm.newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    sitePasswordMutation.mutate({
      newPassword: sitePasswordForm.newPassword
    });
  };

  const handleAdminPasswordSubmit = (e) => {
    e.preventDefault();
    if (adminPasswordForm.newPassword !== adminPasswordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (adminPasswordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    adminPasswordMutation.mutate({
      currentPassword: adminPasswordForm.currentPassword,
      newPassword: adminPasswordForm.newPassword
    });
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await api.exportData();
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guidebook-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!data.version) {
        toast.error('Invalid backup file format');
        return;
      }

      const response = await api.importData(data);
      toast.success(`Import successful! ${response.data.imported.categories} categories, ${response.data.imported.sections} sections, ${response.data.imported.files} files`);

      // Refresh all data
      queryClient.invalidateQueries();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to import data');
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
      e.target.value = ''; // Reset file input
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lodge-brown-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-display text-lodge-brown-800">Settings</h1>
      </div>

      <div className="space-y-8">
        {/* General Settings */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-mountain-gray-900 mb-4">
              General Settings
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  House Name
                </label>
                <input
                  type="text"
                  value={formData.house_name}
                  onChange={(e) => setFormData({ ...formData, house_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Welcome Message
                </label>
                <input
                  type="text"
                  value={formData.welcome_message}
                  onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Contact
                </label>
                <input
                  type="text"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                  placeholder="Name: (xxx) xxx-xxxx"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  House Manager Email
                </label>
                <input
                  type="email"
                  value={formData.house_manager_email}
                  onChange={(e) => setFormData({ ...formData, house_manager_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                />
              </div>
            </div>

            {/* Notification Settings */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-mountain-gray-900 mb-4">
                Notification Settings
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enable_notifications"
                    checked={formData.enable_notifications}
                    onChange={(e) => setFormData({ ...formData, enable_notifications: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="enable_notifications" className="text-sm font-medium text-gray-700">
                    Enable email notifications when sections are accessed
                  </label>
                </div>

                {formData.enable_notifications && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notification Email
                    </label>
                    <input
                      type="email"
                      value={formData.notification_email}
                      onChange={(e) => setFormData({ ...formData, notification_email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                      placeholder="admin@example.com"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                type="submit"
                className="btn-primary flex items-center"
                disabled={updateMutation.isLoading}
              >
                <FiSave className="mr-2" />
                {updateMutation.isLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </form>

        {/* Security Settings */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-mountain-gray-900 mb-6 flex items-center">
            <FiShield className="mr-2" />
            Security Settings
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Site Password */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-mountain-gray-900 mb-4 flex items-center">
                <FiLock className="mr-2 text-lodge-brown-600" />
                Site Password
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                This password is required for visitors to access the guidebook.
              </p>
              <form onSubmit={handleSitePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={sitePasswordForm.newPassword}
                    onChange={(e) => setSitePasswordForm({ ...sitePasswordForm, newPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                    required
                    minLength={4}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={sitePasswordForm.confirmPassword}
                    onChange={(e) => setSitePasswordForm({ ...sitePasswordForm, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full btn-primary flex items-center justify-center"
                  disabled={sitePasswordMutation.isLoading}
                >
                  <FiSave className="mr-2" />
                  {sitePasswordMutation.isLoading ? 'Updating...' : 'Update Site Password'}
                </button>
              </form>
            </div>

            {/* Admin Password */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-mountain-gray-900 mb-4 flex items-center">
                <FiLock className="mr-2 text-lodge-brown-600" />
                Admin Password
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Change the password for your admin account.
              </p>
              <form onSubmit={handleAdminPasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={adminPasswordForm.currentPassword}
                    onChange={(e) => setAdminPasswordForm({ ...adminPasswordForm, currentPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={adminPasswordForm.newPassword}
                    onChange={(e) => setAdminPasswordForm({ ...adminPasswordForm, newPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                    required
                    minLength={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={adminPasswordForm.confirmPassword}
                    onChange={(e) => setAdminPasswordForm({ ...adminPasswordForm, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full btn-primary flex items-center justify-center"
                  disabled={adminPasswordMutation.isLoading}
                >
                  <FiSave className="mr-2" />
                  {adminPasswordMutation.isLoading ? 'Updating...' : 'Update Admin Password'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Data Management */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-mountain-gray-900 mb-6 flex items-center">
            <FiDatabase className="mr-2" />
            Data Management
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Export */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-mountain-gray-900 mb-4 flex items-center">
                <FiDownload className="mr-2 text-lodge-brown-600" />
                Export Data
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Download a backup of all content including categories, sections, settings, and uploaded images.
              </p>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="w-full btn-primary flex items-center justify-center"
              >
                <FiDownload className="mr-2" />
                {isExporting ? 'Exporting...' : 'Export All Data'}
              </button>
            </div>

            {/* Import */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-mountain-gray-900 mb-4 flex items-center">
                <FiUpload className="mr-2 text-lodge-brown-600" />
                Import Data
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Restore data from a backup file. This will replace all existing content (except passwords).
              </p>
              <label className="w-full btn-primary flex items-center justify-center cursor-pointer">
                <FiUpload className="mr-2" />
                {isImporting ? 'Importing...' : 'Import Backup File'}
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  disabled={isImporting}
                  className="hidden"
                />
              </label>
              <p className="text-xs text-red-600 mt-2">
                Warning: Import will overwrite existing content!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
